// Thin wrapper around the LiveKit JS SDK.
// Manages connect/disconnect and exposes the Room object so components
// can subscribe to tracks without each importing the SDK directly.

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Room,
  RoomEvent,
  RemoteParticipant,
  RemoteTrackPublication,
  RemoteTrack,
  Track,
  createLocalAudioTrack,
  LocalAudioTrack,
  LocalTrack,
} from 'livekit-client';

interface UseLiveKitRoomOptions {
  url: string | null;
  token: string | null;
  /** MediaStream from the Web Audio backing track — published as a virtual mic */
  backingTrackStream?: MediaStream | null;
}

interface LiveKitRoomState {
  room: Room | null;
  isConnected: boolean;
  remoteParticipants: RemoteParticipant[];
  /** Attach a remote track's MediaStreamTrack to a <video> or <audio> element */
  attachTrack: (track: RemoteTrack, element: HTMLMediaElement) => void;
  /** Publish the backing track stream as a local audio track */
  publishBackingTrack: (stream: MediaStream) => Promise<void>;
  unpublishBackingTrack: () => Promise<void>;
}

export function useLiveKitRoom({
  url,
  token,
  backingTrackStream,
}: UseLiveKitRoomOptions): LiveKitRoomState {
  const roomRef = useRef<Room | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [remoteParticipants, setRemoteParticipants] = useState<RemoteParticipant[]>([]);
  const backingTrackRef = useRef<LocalAudioTrack | null>(null);

  const refreshParticipants = useCallback(() => {
    if (!roomRef.current) return;
    setRemoteParticipants(Array.from(roomRef.current.remoteParticipants.values()));
  }, []);

  useEffect(() => {
    if (!url || !token) return;

    const room = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;

    room
      .on(RoomEvent.Connected, () => {
        setIsConnected(true);
        refreshParticipants();
      })
      .on(RoomEvent.Disconnected, () => {
        setIsConnected(false);
        setRemoteParticipants([]);
      })
      .on(RoomEvent.ParticipantConnected, refreshParticipants)
      .on(RoomEvent.ParticipantDisconnected, refreshParticipants)
      .on(RoomEvent.TrackSubscribed, refreshParticipants)
      .on(RoomEvent.TrackUnsubscribed, refreshParticipants);

    room.connect(url, token).catch(console.error);

    return () => {
      room.disconnect();
      roomRef.current = null;
      setIsConnected(false);
      setRemoteParticipants([]);
    };
  }, [url, token, refreshParticipants]);

  const attachTrack = useCallback((track: RemoteTrack, el: HTMLMediaElement) => {
    track.attach(el);
  }, []);

  const publishBackingTrack = useCallback(async (stream: MediaStream) => {
    if (!roomRef.current) return;
    const mediaTrack = stream.getAudioTracks()[0];
    if (!mediaTrack) return;
    const localAudio = new LocalAudioTrack(mediaTrack);
    backingTrackRef.current = localAudio;
    await roomRef.current.localParticipant.publishTrack(localAudio, {
      name: 'backing-track',
      source: Track.Source.Microphone,
    });
  }, []);

  const unpublishBackingTrack = useCallback(async () => {
    if (!roomRef.current || !backingTrackRef.current) return;
    await roomRef.current.localParticipant.unpublishTrack(backingTrackRef.current);
    backingTrackRef.current = null;
  }, []);

  return {
    room: roomRef.current,
    isConnected,
    remoteParticipants,
    attachTrack,
    publishBackingTrack,
    unpublishBackingTrack,
  };
}
